import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { Placeholder } from './placeholder-node';
import { Checkbox } from './checkbox-node';
import { DateField } from './date-node';
import { Signature } from './signature-node';
import Gapcursor from '@tiptap/extension-gapcursor';
import { TextStyle } from '@tiptap/extension-text-style';
import { LockedClause } from '../locked-clause';
import { SpecialConditions } from '../special-conditions-node';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import { Annexure } from '../annexus-node';
import { TextField } from '../text-node';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { RadioGroupViewerNode } from './radio-button-viewer';

export default function DocViewer({
  data,
  activePage,
  onEditorReady,
  readOnly = true,
}) {
  const [activeFieldInfo, setActiveFieldInfo] = useState(null);
  const page =
    data && data.pages
      ? data.pages.find((p) => p.id === activePage)
      : undefined;
  const removeCommentMarks = (node) => {
    if (!node) return node;

    const copy = JSON.parse(JSON.stringify(node));

    if (copy.marks) {
      copy.marks = copy.marks.filter((mark) => mark.type !== 'commentMark');

      if (copy.marks.length === 0) {
        delete copy.marks;
      }
    }

    if (copy.content && Array.isArray(copy.content)) {
      copy.content = copy.content.map(removeCommentMarks);
    }

    return copy;
  };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: false,
      }),
      TextStyle,
      Placeholder,
      Checkbox,
      DateField,
      Signature,
      SpecialConditions,
      Gapcursor,
      LockedClause,
      Highlight,
      RadioGroupViewerNode,
      Underline,
      Link.configure({
        openOnClick: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TableRow,
      TableCell,
      TableHeader,
      TextField,
      Annexure,
      BulletList,
      OrderedList,
      ListItem,
      Table.configure({
        resizable: false,
      }),
    ],

    content: {
      type: 'doc',
      content: [],
    },

    // 🔒 Completely disable editing
    editable: false,

    editorProps: {
      attributes: {
        class: 'pointer-events-none select-text',
      },

      handleKeyDown() {
        return true; // block keyboard completely
      },

      handlePaste() {
        return true; // block paste
      },

      handleDrop() {
        return true; // block drag/drop
      },

      handleTextInput() {
        return true; // block typing
      },

      handleDOMEvents: {
        beforeinput() {
          return true; // block browser input
        },

        keydown() {
          return true;
        },

        click(view, event) {
          // prevent focusing nodes
          event.preventDefault();
          return false;
        },
      },
    },

    onCreate({ editor }) {
      onEditorReady?.(editor);
    },
  });
  useEffect(() => {
    if (!editor) return;

    const handler = (e) => {
      const target = e.target.closest('[data-placeholder-id]');

      if (target) {
        const id = target.getAttribute('data-placeholder-id');
        const label = target.getAttribute('data-label');
        const page = target.getAttribute('data-page');

        setActiveFieldInfo({
          id,
          label,
          page,
          x: e.clientX,
          y: e.clientY,
        });
      }
    };

    document.addEventListener('click', handler);

    return () => document.removeEventListener('click', handler);
  }, [editor]);
  useEffect(() => {
    if (!editor || !page?.content) return;

    const cleanContent = removeCommentMarks(page.content);

    editor.commands.setContent(cleanContent, false);

    setTimeout(() => {
      onEditorReady?.(editor);
    }, 100);
  }, [editor, page]);

  return (
    <div className="flex justify-center py-8 px-4">
      {' '}
      <div
        className="
        w-full max-w-[900px]
        bg-white
        rounded-xl
        shadow-[0_10px_40px_rgba(0,0,0,0.08)]
        border border-gray-200

        p-8 sm:p-10 md:p-14
      "
      >
        <div className="prose prose-gray">
          <EditorContent editor={editor} />
        </div>
      </div>
      {activeFieldInfo && (
        <div
          className="
    fixed
    z-50
    bg-white
    shadow-xl
    rounded-xl
    px-4
    py-3
    text-xs
    border
    border-slate-200
    backdrop-blur-lg
    "
          style={{
            top: activeFieldInfo.y + 10,
            left: activeFieldInfo.x + 10,
          }}
        >
          <div className="font-semibold text-slate-700">
            {activeFieldInfo.label}
          </div>

          <div className="text-slate-500 mt-1">Page {activeFieldInfo.page}</div>
        </div>
      )}
    </div>
  );
}
